import { API_CONFIG, SESSION_KEYS } from './config';
// The workspace id lives in TWO channels (localStorage for this client, a
// cookie for every Next route handler / server component). Writing one without
// the other is what let a page read two different tenants at once, so every
// write in the app goes through these two helpers — including the resurrect in
// getWorkspaceId() below, which was the last raw writer left.
import { persistWorkspaceId, clearWorkspaceId } from '@/lib/tenant/workspace-id-storage';
// The access token lives in the SAME two channels, and the refresh below used
// to write only one of them. See access-token-storage.ts for the full failure.
import { persistAccessToken, clearAccessToken } from '@/lib/tenant/access-token-storage';
import { isImpersonationExiting } from './impersonation-exit';
import { hardNavigate } from '@/lib/auth/hard-navigate';
import { sessionEndedLoginHref } from '@/lib/status/session-ended';

/** Flag to prevent multiple concurrent refresh attempts */
let isRefreshing = false;
let refreshPromise: Promise<string | null> | null = null;

/** Flag to prevent multiple simultaneous logout redirects */
let isLoggingOut = false;

/**
 * Where a terminal 401 sends the operator.
 *
 * Exported and pure because jsdom (26) makes `window.location` and its `href`
 * non-configurable, so a test cannot intercept the assignment itself — the
 * decision is testable, the navigation is not. Keep the mapping here rather
 * than inline at the call site so it stays that way.
 *
 * `company_disabled` has to survive the hard navigation, and only the URL can
 * carry it: a toast is destroyed by `location.href =`. Landing on a bare
 * /login is what made the 2026-09-17 report look like nothing had happened —
 * the page's own title reads "Welcome back".
 *
 * `endedOn` is the page the person was on when the session ENDED (the refresh
 * was refused or there was no refresh token). It travels as `?next=`, so the
 * sign-in page shows the session-ended picture and signing in returns there
 * (owner, 2026-10-06; sessionEndedLoginHref decides which pages qualify).
 * Omitted — a bare /login — when the refresh WORKED and the request still
 * 401'd: the session is alive and that endpoint refuses it, so sending the
 * person back after signing in would sign them out again, every time. A
 * deactivated company keeps its own explanation, without the picture.
 */
export function unauthorizedRedirectTarget(
  errorCode: unknown,
  endedOn?: { pathname: string; search?: string } | null,
): string {
  if (errorCode === 'company_disabled') return '/login?reason=company_disabled';
  return endedOn ? sessionEndedLoginHref(endedOn) : '/login';
}

/**
 * Attempt to refresh the access token using the stored refresh token.
 * Returns the new access token on success, or null on failure.
 */
async function tryRefreshToken(): Promise<string | null> {
  // Deduplicate concurrent refresh calls
  if (isRefreshing && refreshPromise) {
    return refreshPromise;
  }

  const refreshToken =
    typeof window !== 'undefined'
      ? localStorage.getItem('hf-app:refresh-token')
      : null;

  if (!refreshToken) return null;

  isRefreshing = true;
  refreshPromise = (async () => {
    try {
      const res = await fetch(
        `${API_CONFIG.baseUrl}${API_CONFIG.versionPrefix}/auth/token/refresh`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ refreshToken }),
        },
      );

      if (!res.ok) return null;

      const data = await res.json();
      const newToken: string | undefined = data.accessToken;

      if (newToken && typeof window !== 'undefined') {
        // BOTH channels. This was `localStorage.setItem` alone, so the rotated
        // token reached the browser client and never reached the
        // `hf-app:access-token` COOKIE — the only half a Next route handler can
        // read. The refresh fires on the 8h token expiry inside a 7-day
        // session; the cookie carries a 24h max-age and nothing else re-armed
        // it in a tab that never reloads. At 24h `adminAuth()` started
        // returning null and every server-side PhishTrainer proxy degraded to
        // its empty fallback while the client half kept working.
        persistAccessToken(newToken);
      }

      return newToken ?? null;
    } catch {
      return null;
    } finally {
      isRefreshing = false;
      refreshPromise = null;
    }
  })();

  return refreshPromise;
}

export async function refreshAccessToken(): Promise<string | null> {
  return tryRefreshToken()
}

export class ApiClientError extends Error {
  /**
   * Machine-readable failure code from the backend body, verbatim — the REST
   * key `errorCode`, or the SSE-frame key `code`. Null when the body carries
   * neither.
   *
   * `statusCode` + `body` were already retained, but nothing ever read the code
   * out of them, so every caller that wanted to branch on WHICH failure
   * happened had to re-parse `body` itself (and none did). The AI surfaces need
   * it in particular: the monthly-credit ceiling is `quota_exhausted_use_own_key`
   * on an HTTP **400**, so status cannot identify it. Lift this into the typed
   * AI contract with `toAiError()` from `@/lib/ai/errors`.
   */
  readonly errorCode: string | null;

  constructor(
    public statusCode: number,
    public statusText: string,
    public body?: unknown,
  ) {
    // Extract the human-friendly message from the backend response body.
    // NestJS ValidationPipe returns { statusCode, message } (string | string[]),
    // but hand-thrown BadRequest/NotFound bodies key the reason as `error`
    // (+ an optional `issues[]` array with per-item `message`s — e.g. workflow
    // activation gates). Without reading those, a custom 400/404 used to fall
    // back to `API Error 400:`/`API Error 404:` (empty statusText) → a
    // useless toast.
    const backendMessage = ApiClientError.extractMessage(body);
    // S7 — last-resort fallback, when the body carries NO readable reason at
    // all (a bare 500 from an nginx error page, or an unhandled exception
    // with a plain-text/HTML body rather than NestJS's usual `{ message }`
    // envelope — `body` arrives as a STRING in that case, via fetchApi's
    // `response.text()` fallback). This used to be
    // `API Error ${statusCode}: ${statusText}` — a raw HTTP status code and
    // reason phrase, rendered verbatim wherever a caller shows `.message`
    // (the AI-chat confirm dialog among them). Product explicitly bans
    // technical strings in user-facing copy; `statusCode`/`statusText` stay
    // available as properties on the thrown instance for any caller that
    // wants the raw diagnostic — only the DISPLAY string changes.
    super(backendMessage || 'Something went wrong. Please try again.');
    this.name = 'ApiClientError';
    this.errorCode = ApiClientError.extractErrorCode(body);
  }

  /** Read `errorCode` (REST) / `code` (SSE frame) off the body. */
  private static extractErrorCode(body: unknown): string | null {
    if (!body || typeof body !== 'object') return null;
    const rec = body as Record<string, unknown>;
    for (const key of ['errorCode', 'code'] as const) {
      const value = rec[key];
      if (typeof value === 'string' && value.trim()) return value.trim();
    }
    return null;
  }

  /**
   * Pull the best human-readable reason out of a NestJS error body, in order:
   *   1. `message` (ValidationPipe) — string | string[]
   *   2. `error`   (hand-thrown BadRequest/NotFound) — string
   *   3. `issues[].message` (activation/validation gates) — joined
   *   4. `errorCode` / `code` — the raw code, as a last resort. A body that
   *      keys its reason ONLY as `errorCode` (the AI Studio quota guard shape)
   *      used to fall through to "API Error 400: Bad Request", which tells the
   *      operator nothing at all.
   * Returns undefined when the body carries none of these.
   */
  private static extractMessage(body: unknown): string | undefined {
    if (!body || typeof body !== 'object') return undefined;
    const rec = body as Record<string, unknown>;

    if ('message' in rec) {
      const m = rec.message;
      if (Array.isArray(m)) {
        const joined = m.filter((x) => typeof x === 'string').join('. ');
        if (joined) return joined;
      } else if (typeof m === 'string' && m) {
        return m;
      }
    }

    // Collect issue lines up front so `error` can lead them (e.g.
    // "Fix 2 issues before turning this on: …") for the fullest toast.
    const issueLines = Array.isArray(rec.issues)
      ? rec.issues
          .map((i) =>
            i && typeof i === 'object' && typeof (i as Record<string, unknown>).message === 'string'
              ? ((i as Record<string, unknown>).message as string)
              : null,
          )
          .filter((x): x is string => !!x)
      : [];

    const headline = typeof rec.error === 'string' && rec.error ? rec.error : undefined;

    if (headline && issueLines.length) return `${headline}: ${issueLines.join('. ')}`;
    if (headline) return headline;
    if (issueLines.length) return issueLines.join('. ');
    return ApiClientError.extractErrorCode(body) ?? undefined;
  }
}

/**
 * Universal API client → the NestJS backend at API_CONFIG.baseUrl + /v1.
 *
 * skipAuth (portal hooks, OTP login, onboarding bootstrap) bypasses the
 * admin token + workspace header entirely.
 */
/**
 * H7: does this 403/404 say the stored workspace is gone (deleted, removed, reseeded)? Read off the message, because
 * the guards that say so (TenantContextGuard 'Access denied to this workspace', 'Workspace not found') carry no
 * errorCode. A body WITH an errorCode is a module refusing on its own terms, even when its sentence mentions the
 * workspace: Search & Remediate's 'Permanent delete is switched off for this workspace.' cleared the workspace and
 * threw the analyst to the dashboard (release-2 QA, 28 Oct 2026). That is never the stale-workspace case.
 */
export function staleWorkspaceRefusal(status: number, body: unknown): boolean {
  if (status !== 403 && status !== 404) return false;
  if (body && typeof body === 'object') {
    const code = (body as { errorCode?: unknown }).errorCode;
    if (typeof code === 'string' && code.trim() !== '') return false;
  }
  const bodyMessage =
    body && typeof body === 'object' && 'message' in body
      ? Array.isArray((body as { message: unknown }).message)
        ? ((body as { message: string[] }).message).join('. ')
        : String((body as { message: unknown }).message)
      : typeof body === 'string'
        ? body
        : '';
  return (
    (status === 403 && /workspace|tenant|access denied/i.test(bodyMessage)) ||
    (status === 404 && /workspace not found/i.test(bodyMessage))
  );
}

export async function fetchApi<T>(
  endpoint: string,
  options?: RequestInit & { skipAuth?: boolean },
): Promise<T> {
  const url = buildUrl(endpoint);

  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(options?.headers as Record<string, string>),
  };

  if (!options?.skipAuth) {
    const token = getAccessToken();
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
    const workspaceId = getWorkspaceId();
    if (workspaceId) {
      headers['x-workspace-id'] = workspaceId;
    }
  }

  const response = await fetch(url, {
    ...options,
    headers,
  });

  if (!response.ok) {
    // Auto-refresh and retry on 401 in production mode.
    //
    // skipAuth requests are explicit "don't touch admin auth" calls
    // (portal hooks, OTP login, onboarding bootstrap). Those carry
    // their own bearer (portal JWT) or run anonymously. Treating their
    // 401 as an admin-session-expired signal nukes the admin token,
    // wipes cookies, and force-redirects to /login — which booted the
    // employee out of /portal back to the admin login page.
    //
    // Behaviour on 401 + skipAuth: throw a clean ApiClientError(401)
    // so the caller can decide what to do (show toast, redirect to
    // /portal/login, etc). DO NOT mutate admin auth state.
    if (response.status === 401 && !options?.skipAuth) {
      // Read the server's reason BEFORE the force-logout path throws it away.
      // Safe to consume here: the refresh and retry below use their own
      // Response objects, and the `response.json()` further down is only
      // reached when this branch does not throw.
      //
      // Without this the client replaced every 401 body with the hardcoded
      // "Session expired", so a company-disabled refusal — which the API
      // stamps with errorCode 'company_disabled' — reached the user as a
      // silent bounce to /login with no explanation (2026-09-17).
      let unauthorizedBody: unknown;
      try {
        unauthorizedBody = await response.clone().json();
      } catch {
        unauthorizedBody = undefined;
      }
      const unauthorizedCode =
        typeof unauthorizedBody === 'object' && unauthorizedBody !== null
          ? (unauthorizedBody as { errorCode?: unknown }).errorCode
          : undefined;

      // 401: try refresh, then force logout if that fails.
      const refreshed = await tryRefreshToken();
      if (refreshed) {
        // Retry the original request with the new token
        headers['Authorization'] = `Bearer ${refreshed}`;
        const retryResponse = await fetch(url, { ...options, headers });
        if (retryResponse.ok) {
          const ct = retryResponse.headers.get('content-type');
          if (!ct || !ct.includes('application/json')) {
            return undefined as T;
          }
          return retryResponse.json();
        }
      }
      // Refresh failed or retry failed — force logout (deduplicated).
      //
      // Loop guard (P0 fix 2026-05-06): if the user is ALREADY on
      // /login (or any /auth/* page), DO NOT redirect — that re-fires
      // the redirect every time the page mounts a query and 401s,
      // making the URL bar flash /login → /login → /login. Just throw
      // the error; the page itself can render a "session expired"
      // message and let the user re-login.
      // Routes where an admin-token 401 must NOT nuke admin auth + redirect to
      // /login. Besides the login surfaces, this covers the non-admin realms:
      // the employee portal (/portal) and the PhishTrainer player (/play). Those
      // run on a recipient JWT, so a stray admin-scoped 401 there (e.g. a shared
      // guard calling /me/modules) is expected and must be ignored — otherwise
      // the learner gets booted to the admin login page.
      const onLoginRoute =
        typeof window !== 'undefined' &&
        (window.location.pathname === '/login' ||
          window.location.pathname.startsWith('/auth/') ||
          window.location.pathname.startsWith('/portal') ||
          window.location.pathname.startsWith('/play') ||
          // The outside auditor's area (Policies v3): it runs on its own
          // X-Policy-Audit-Token, never the admin session. A stale admin token
          // in the same browser 401s on the shell's /me/modules; that must not
          // sign the admin out or throw the auditor to /login mid-review (R4 M6).
          window.location.pathname === '/policy-audit' ||
          window.location.pathname.startsWith('/policy-audit/') ||
          // Public invite-acceptance surface: it manages identity itself
          // (register / sign-out-and-continue / stale-token recovery). A stray
          // 401 here must surface to the page's own catch, not hijack the
          // invitee to /login mid-flow.
          window.location.pathname.startsWith('/accept-invite'));

      // An impersonation exit in progress is not an expired session. Ending an
      // impersonation now REVOKES the session server-side (RC-F — /end used to
      // be audit-only), so every TanStack refetch still in flight 401s while the
      // banner is mid-teardown. Without this guard the force-logout below wins
      // the race and dumps the super-admin on the CUSTOMER /login page — the
      // customer shell is not in the exempt-path list above — instead of the
      // /admin destination the banner is already navigating to.
      const exitingImpersonation = isImpersonationExiting();

      if (
        typeof window !== 'undefined' &&
        !isLoggingOut &&
        !onLoginRoute &&
        !exitingImpersonation
      ) {
        isLoggingOut = true;
        // Token: both channels, through the one helper that owns them.
        clearAccessToken();
        localStorage.removeItem(SESSION_KEYS.REAL_REFRESH_KEY);
        // Both channels, and the zustand blob that can rebuild them.
        //
        // This used to be a bare removeItem of the localStorage key, which left
        // the `hf-app:workspace-id` COOKIE — the half every route handler reads
        // — pointing at the dead session's workspace. And 'hf-app:workspace'
        // (the persisted workspace-store) was in NO teardown list anywhere, so
        // the very next call to getWorkspaceId() read the old id straight back
        // out of it and re-published it to both channels.
        clearWorkspaceId();
        localStorage.removeItem('hf-app:workspace');
        localStorage.removeItem('hf-app:auth-store');
        // Clear cookies so middleware doesn't bounce back
        document.cookie = 'hf-app:onboarding-complete=; path=/; max-age=0';
        // The reason has to travel in the URL. A toast cannot survive
        // window.location.href — which is exactly why the old behaviour
        // flashed something and left the user staring at a bare login form.
        // So does the page: a refused refresh means the session ended here.
        hardNavigate(
          unauthorizedRedirectTarget(unauthorizedCode, refreshed ? null : window.location),
        );
      }
      throw new ApiClientError(
        401,
        'Unauthorized',
        // ApiClientError lifts `errorCode` out of the body, so callers that
        // stay on the page get the real reason instead of "Session expired".
        unauthorizedBody ?? { message: 'Session expired' },
      );
    }

    let body: unknown;
    try {
      body = await response.json();
    } catch {
      body = await response.text();
    }

    // H7 — Stale X-Workspace-Id header: when a 403/404 is caused by the
    // workspace header pointing to a workspace the user no longer has access
    // to (deleted, removed, reseeded), clear the stored id and reload from the
    // root so the TeamSwitcher re-resolves /workspaces/my. Only runs in
    // production mode with an x-workspace-id header present, and only if the
    // response body hints at "workspace" / "tenant" as the cause (so we don't
    // clear on unrelated 403/404), and never for a body with an errorCode (a
    // module's own refusal: staleWorkspaceRefusal).
    if (
      (response.status === 403 || response.status === 404) &&
      headers['x-workspace-id'] &&
      typeof window !== 'undefined' &&
      !isLoggingOut
    ) {
      if (staleWorkspaceRefusal(response.status, body)) {
        // BOTH channels, not just localStorage.
        //
        // This was a bare `removeItem('hf-app:workspace-id')`, which left the
        // `hf-app:workspace-id` COOKIE intact — and the cookie is the half a
        // Next route handler / server component reads. So between this bounce
        // and the TeamSwitcher re-resolving /workspaces/my, every server-side
        // call still carried the workspace that had just 403'd: a strict
        // regression from before the cookie existed, when that call carried a
        // still-valid JWT claim instead.
        clearWorkspaceId();
        // Also clear the zustand-persist workspace store so getWorkspaceId()
        // doesn't resurrect the stale id on the next request.
        try {
          const raw = localStorage.getItem('hf-app:workspace');
          if (raw) {
            const parsed = JSON.parse(raw);
            if (parsed?.state) {
              parsed.state.activeWorkspaceId = null;
              localStorage.setItem('hf-app:workspace', JSON.stringify(parsed));
            }
          }
        } catch {
          // ignore malformed JSON
        }
        // Reload from the root. There is no WorkspaceGate and no middleware —
        // an earlier version of this block dispatched an `hf-app:workspace-invalid`
        // CustomEvent and said a gate would "redirect to the picker"; grep found
        // no listener and no such component anywhere in apps/web, so the event
        // was dead code describing a screen that was never built. What actually
        // recovers the session is this full reload with both channels cleared:
        // AuthHydrationProvider re-runs and the TeamSwitcher re-resolves
        // /workspaces/my. Do not re-add a dispatch without a listener.
        window.location.href = '/';
        throw new ApiClientError(response.status, response.statusText, body);
      }
    }

    throw new ApiClientError(response.status, response.statusText, body);
  }

  // Handle empty responses (204 No Content, etc.)
  const contentType = response.headers.get('content-type');
  if (!contentType || !contentType.includes('application/json')) {
    return undefined as T;
  }

  return response.json();
}

function buildUrl(endpoint: string): string {
  // Supply a missing leading slash BEFORE the /api/ strip, so 'x' and '/x'
  // build the same URL. Without it the endpoint glued onto the version prefix:
  // 'auth/super-admin/security' became /v1auth/super-admin/security, a 404 that
  // left the super-admin Security page unable to load or save its policy from
  // 498b51bfd (2026-08-06) until 2026-09-25.
  const withSlash = endpoint.startsWith('/') ? endpoint : `/${endpoint}`;
  const cleanEndpoint = withSlash.replace(/^\/api\//, '/');
  return `${API_CONFIG.baseUrl}${API_CONFIG.versionPrefix}${cleanEndpoint}`;
}

export function getAccessToken(): string | null {
  if (typeof window === 'undefined') return null;
  return localStorage.getItem(SESSION_KEYS.REAL_TOKEN_KEY);
}

export function getWorkspaceId(): string | null {
  if (typeof window === 'undefined') return null;

  // Primary key set by auth-store.login() + workspace-store.setActiveWorkspace().
  const direct = localStorage.getItem(SESSION_KEYS.REAL_WORKSPACE_KEY);
  if (direct) return direct;
  // Fallback: zustand workspace persist store (race-condition guard for
  // TeamSwitcher pre-hydration / OAuth callback that skips workspace fetch).
  try {
    const raw = localStorage.getItem('hf-app:workspace');
    if (raw) {
      const parsed = JSON.parse(raw);
      const id = parsed?.state?.activeWorkspaceId;
      if (id && id !== 'all') {
        // Sync back through the ONE writer, not a raw setItem.
        //
        // This was the last raw `localStorage.setItem(REAL_WORKSPACE_KEY, …)`
        // in the app after 69022ffd routed login, the OAuth callback and
        // xhr-upload through persistWorkspaceId. Because it wrote only the
        // localStorage half, this resurrect re-created the header channel while
        // the cookie stayed cleared — so the browser sent x-workspace-id for
        // workspace X and every Next route handler on the same page fell back
        // to the JWT claim and answered for workspace Y. That is precisely the
        // split the single-writer refactor existed to make impossible.
        persistWorkspaceId(id);
        return id;
      }
    }
  } catch {
    // ignore malformed JSON
  }
  return null;
}

// Helper to build query strings from filter objects
export function buildQueryString(params?: Record<string, unknown>): string {
  if (!params) return '';
  const searchParams = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && value !== null && value !== '') {
      if (Array.isArray(value)) {
        value.forEach(v => searchParams.append(key, String(v)));
      } else {
        searchParams.set(key, String(value));
      }
    }
  }
  const qs = searchParams.toString();
  return qs ? `?${qs}` : '';
}
